import {IRowVersionableDto} from '../common/rowVersionable';
import {Address} from '../common/address';
import { TenderType } from '../tenders/tender.enums';
import { NoticeStatus } from '../notices/notice.enums';

export class UpdateMatchPreferencesDto {
    categoryIds: Array<number>;
    locationIds: Array<number>;
    userId?: number;
}

export class UpdateAwardsDto {
    supplierId: number;
}

export class MatchPreferencesDto {
    categoryIds: Array<number>;
    locationIds: Array<number>;
}

export class AddExtraAlertDto {
    categoryIds: Array<number>;
    locationIds: Array<number>;
    supplierId: number;
    description: string;
    includeAwards: boolean;
}

export class UpdateExtraAlertDto {
    id: number;
    categoryIds: Array<number>;
    locationIds: Array<number>;
    supplierId: number;
    description: string;
    includeAwards: boolean;
}

export class GetExtraAlertsDto {
    id: number;
    supplierId: number;
    description: string;
    rowVersion: string;
    categoryIds: Array<number>;
    locationIds: Array<number>;
    includeAwards: boolean;
}

export class GetEmailPreferencesDto {
    newsletters: boolean;
    hvtAlerts: boolean;
    lvtAlerts: boolean;
    pvtAlerts: boolean;
    hvcaAlerts: boolean;
    lvcaAlerts: boolean;
    sixMonthsAlerts: boolean;
    cumulativeAlerts: boolean;
    extraAlerts: boolean;
}

export class UpdateEmailPreferencesDto {
    newsletters: boolean;
    hvtAlerts: boolean;
    lvtAlerts: boolean;
    pvtAlerts: boolean;
    hvcaAlerts: boolean;
    lvcaAlerts: boolean;
    sixMonthsAlerts: boolean;
    feedback: string;
    extraAlerts: boolean;

    constructor(preferences: GetEmailPreferencesDto, feedback: string) {
        this.newsletters = preferences.newsletters;
        this.hvtAlerts = preferences.hvtAlerts;
        this.lvtAlerts = preferences.lvtAlerts;
        this.pvtAlerts = preferences.pvtAlerts;
        this.hvcaAlerts = preferences.hvcaAlerts;
        this.lvcaAlerts = preferences.lvcaAlerts;
        this.sixMonthsAlerts = preferences.sixMonthsAlerts;
        this.feedback = feedback;
        this.extraAlerts = preferences.extraAlerts;
    }
}

export class GetAccountDetailsDto implements IRowVersionableDto {
    rowVersion: string;
    firstName: string;
    lastName: string;
    companyName: string;
    emailAddressPrefix: string;
    emailAddressSuffix: string;
    phoneNumber: string;
    accountStatus: string;
    address: Address;
}

export class DocumentsAcknowledgedDto {
    documentsAcknowledged: boolean;
    documentsAcknowledgedDate: Date;
    jsDocumentsAcknowledgedDate: string;
}

export class UpdateAccountDetailsDto implements IRowVersionableDto {
    rowVersion: string;
    id: number;
    firstName: string;
    lastName: string;
    companyName: string;
    emailAddressPrefix: string;
    phoneNumber: string;
    address: Address;
    validatePassword: string;
    newPassword: string;
    confirmNewPassword: string;

    constructor(accountDetails: GetAccountDetailsDto) {
        this.rowVersion = accountDetails.rowVersion;
        this.firstName = accountDetails.firstName;
        this.lastName = accountDetails.lastName;
        this.companyName = accountDetails.companyName;
        this.emailAddressPrefix = accountDetails.emailAddressPrefix;
        this.phoneNumber = accountDetails.phoneNumber;
        this.address = new Address();
        this.address.line1 = accountDetails.address.line1;
        this.address.line2 = accountDetails.address.line2;
        this.address.city = accountDetails.address.city;
        this.address.postcode = accountDetails.address.postcode;
        this.rowVersion = accountDetails.rowVersion;
    }
}

export class CustomerNewFeaturesDto {
    id: number;
    customerId: number;
    newFeatureId: number;
    createdDate: Date;
    featureSeenDate: Date;
    alreadySeen: boolean;
}

export class MarkNewFeatureReadDto {
    newFeatureId: number;
}

export class GetCustomerSavedSearchesDto {
    id: number;
    noticeId: number;
    customerId: number;
    description: string;
    rowVersion: string;
    categoryIds: Array<number>;
    locationIds: Array<number>;
    types: number[];
    status: NoticeStatus;
    publishedDateFrom: string;
    publishedDateTo: string;
    expiredDateFrom: string;
    expiredDateTo: string;
    registerInterestDeadlineFrom: string;
    registerInterestDeadlineTo: string;
    submitDocumentsDeadlineFrom: string;
    submitDocumentsDeadlineTo: string;
    contractStartDateFrom: string;
    contractStartDateTo: string;
    contractEndDateFrom: string;
    contractEndDateTo: string;
    buyerOrganisation: string;
    buyerName: string;
    buyerEmail: string;
    ojeuId: string;
    keywords: string[];
    excludeKeywords: string[];
    award: boolean;
    awardedCompanyName: string;
    awardedCompanyEmail: string;
    excludeBookmarked: boolean;
    excludeViewed: boolean;
    excludeAssigned: boolean;
}

export class AddCustomerSavedSearchDto {
    customerId: number;
    description: string;
    rowVersion: string;
    categoryIds: Array<number>;
    locationIds: Array<number>;
    types: number[];
    status: NoticeStatus;
    publishedDateFrom: string;
    publishedDateTo: string;
    expiredDateFrom: string;
    expiredDateTo: string;
    registerInterestDeadlineFrom: string;
    registerInterestDeadlineTo: string;
    submitDocumentsDeadlineFrom: string;
    submitDocumentsDeadlineTo: string;
    contractStartDateFrom: string;
    contractStartDateTo: string;
    contractEndDateFrom: string;
    contractEndDateTo: string;
    buyerOrganisation: string;
    buyerName: string;
    buyerEmail: string;
    ojeuId: string;
    keywords: string[];
    excludeKeywords: string[];
    id: number;
    noticeId: number;
    award: boolean;
    awardedCompanyName: string;
    awardedCompanyEmail: string;
    excludeBookmarked: boolean;
    excludeViewed: boolean;
    excludeAssigned: boolean;
}

export class AddCustomerHiddenDialogDto {
    cMSDialogId: number;
}

export class AddConsultationDto {
    supplierId: number;
    consultationDate: string;
    name: string;
    email: string;
    telephone: string;
    currentlySourcing: boolean;
    sourcingWith: string;
    sourcingEndDate: string;
    freePortals: boolean;
    comments: string;
    selectedTimeslot: number;
}

export class GetConsultationDto {
    supplierId: number;
    consultationDate: string;
    name: string;
    email: string;
    telephone: string;
    currentlySourcing: boolean;
    sourcingWith: string;
    sourcingEndDate: string;
    freePortals: boolean;
    comments: string;
    selectedTimeslot: number;
}

export class ConsultationSearchModel {
    date: string;
}

export class ConsultationTimeSlot {
    id: number;
    time: Date;
    formattedTime: string;
}

export class QuestionnaireQuestionDto {
    id: number;
    mandatory: boolean;
    questionText: string;
    answer: string;
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/models/account/account.models.ts